'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate, UpgradePrompt } from '@/components/app'

const watchedArtists = [
  {
    id: 'yayoi-kusama',
    name: 'Yayoi Kusama',
    nationality: 'Japanese',
    tier: 'Blue-chip',
    trend: 18.4,
    lastSale: { work: 'Infinity Nets', hammer: '$8.9M', date: '2 days ago' },
    upcomingLots: 3,
  },
  {
    id: 'jean-michel-basquiat',
    name: 'Jean-Michel Basquiat',
    nationality: 'American',
    tier: 'Blue-chip',
    trend: 12.1,
    lastSale: { work: 'Untitled (Devil)', hammer: '$42.4M', date: '1 week ago' },
    upcomingLots: 5,
  },
]

const alerts = [
  {
    id: 1,
    type: 'price',
    artist: 'Yayoi Kusama',
    artistSlug: 'yayoi-kusama',
    condition: 'Hammer > $5M',
    triggered: 2,
    active: true,
  },
  {
    id: 2,
    type: 'lot',
    artist: 'Jean-Michel Basquiat',
    artistSlug: 'jean-michel-basquiat',
    condition: 'New lot listed',
    triggered: 5,
    active: true,
  },
]

export default function WatchlistPage() {
  const [view, setView] = useState<'artists' | 'alerts'>('artists')
  const { user, watchlistLimit, alertsRemaining } = useAuth()

  const watchlistCount = watchedArtists.length
  const atLimit = watchlistLimit !== Infinity && watchlistCount >= watchlistLimit

  return (
    <div className="px-4 md:px-6 py-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-light mb-2">Watchlist</h1>
          <p className="text-text-secondary text-sm">
            {watchlistCount} artist{watchlistCount !== 1 ? 's' : ''} tracked
            {watchlistLimit !== Infinity && (
              <span className="text-text-tertiary"> / {watchlistLimit} limit</span>
            )}
          </p>
        </div>

        {/* Segmented control */}
        <div className="segmented-control">
          <button
            className={view === 'artists' ? 'active' : ''}
            onClick={() => setView('artists')}
          >
            Artists ({watchlistCount})
          </button>
          <button
            className={view === 'alerts' ? 'active' : ''}
            onClick={() => setView('alerts')}
          >
            Alerts ({alerts.length})
          </button>
        </div>
      </div>

      {view === 'artists' ? (
        <>
          {/* Add artist prompt */}
          {!atLimit && (
            <Link
              href="/app/artists"
              className="card p-4 mb-6 flex items-center justify-center gap-2 border-dashed hover:border-accent-primary hover:bg-accent-primary/5 transition-all group"
            >
              <svg className="w-5 h-5 text-text-tertiary group-hover:text-accent-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-text-secondary group-hover:text-accent-primary transition-colors">
                Add artist to watchlist
              </span>
            </Link>
          )}

          {/* At limit warning */}
          {atLimit && (
            <div className="mb-6">
              <UpgradePrompt
                feature="unlimited-watchlist"
                requiredTier="collector"
                variant="banner"
              />
            </div>
          )}

          {/* Watched artists */}
          {watchedArtists.length > 0 ? (
            <div className="space-y-4">
              {watchedArtists.map((artist, i) => (
                <motion.div
                  key={artist.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.2 }}
                >
                  <div className="card p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Link
                          href={`/app/artists/${artist.id}`}
                          className="font-medium text-base hover:text-accent-primary transition-colors"
                        >
                          {artist.name}
                        </Link>
                        <p className="text-text-tertiary text-sm">{artist.nationality}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className="text-sm font-medium tabular-nums"
                          style={{ color: artist.trend >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                        >
                          {artist.trend >= 0 ? '+' : ''}{artist.trend}%
                        </span>
                        <button className="p-2 rounded-lg hover:bg-page text-text-tertiary transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Last sale */}
                      <div className="flex-1">
                        <p className="text-xs text-text-tertiary mb-1">Last Sale</p>
                        <p className="text-sm font-medium">{artist.lastSale.hammer}</p>
                        <p className="text-xs text-text-tertiary">{artist.lastSale.work} • {artist.lastSale.date}</p>
                      </div>

                      {/* Upcoming lots */}
                      {artist.upcomingLots > 0 && (
                        <div className="text-right">
                          <p className="text-xs text-text-tertiary mb-1">Upcoming</p>
                          <p className="text-sm font-medium text-accent-primary">
                            {artist.upcomingLots} lot{artist.upcomingLots !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto mb-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <h3 className="font-medium text-text-primary mb-2">No artists yet</h3>
              <p className="text-text-tertiary text-sm mb-4">
                Start tracking artists to get alerts when they come to market
              </p>
              <Link
                href="/app/artists"
                className="pill text-sm"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                Browse artists
              </Link>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Alerts remaining */}
          <div className="card p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Alerts this month</p>
              <p className="text-text-tertiary text-xs">
                {alerts.length} active • {alertsRemaining} remaining
              </p>
            </div>
            {alertsRemaining > 0 ? (
              <button
                className="pill text-sm"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                Create Alert
              </button>
            ) : (
              <Link
                href="/get-started"
                className="text-sm font-medium text-accent-primary hover:underline"
              >
                Upgrade for more
              </Link>
            )}
          </div>

          {/* Alerts list */}
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${alert.active ? 'bg-positive' : 'bg-text-tertiary'}`}
                    />
                    <div>
                      <Link
                        href={`/app/artists/${alert.artistSlug}`}
                        className="text-sm font-medium hover:text-accent-primary transition-colors"
                      >
                        {alert.artist}
                      </Link>
                      <p className="text-xs text-text-tertiary">{alert.condition}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-text-tertiary">
                      Triggered {alert.triggered}x
                    </span>
                    <button className="p-2 rounded-lg hover:bg-page text-text-tertiary transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto mb-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h3 className="font-medium text-text-primary mb-2">No alerts yet</h3>
              <p className="text-text-tertiary text-sm mb-4">
                Create alerts to get notified when artists come to market
              </p>
              <button
                className="pill text-sm"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                Create your first alert
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
